export type RecoveryInputSource = 'text' | 'voice';

export interface RecoveryInputDraft {
  source: RecoveryInputSource;
  raw_input_text?: string;
  transcript_text?: string;
  sleep_quality_subjective: number;
  energy_level: number;
  stress_management_score: number;
  notes?: string;
}

export interface RecoveryInputRecord {
  id: string;
  user_id: string;
  source: RecoveryInputSource;
  raw_input_text: string | null;
  transcript_text: string | null;
  sleep_quality_subjective: number;
  energy_level: number;
  stress_management_score: number;
  notes: string | null;
  created_at: string;
}

const POSITIVE_SLEEP_TERMS = [
  'gut geschlafen',
  'durchgeschlafen',
  'erholt',
  'ausgeruht',
  'rested',
  'slept well',
  'great sleep',
];

const NEGATIVE_SLEEP_TERMS = [
  'schlecht geschlafen',
  'kaum geschlafen',
  'wenig geschlafen',
  'unruhig',
  'wach gelegen',
  'müde',
  'restless',
  'bad sleep',
  'tired',
];

const POSITIVE_ENERGY_TERMS = [
  'fit',
  'energiegeladen',
  'motiviert',
  'wach',
  'fresh',
  'energized',
  'ready',
];

const NEGATIVE_ENERGY_TERMS = [
  'erschöpft',
  'kaputt',
  'matt',
  'träge',
  'low energy',
  'drained',
  'fatigued',
  'tired',
];

const POSITIVE_STRESS_TERMS = ['ruhig', 'entspannt', 'gelassen', 'calm', 'balanced', 'centered'];

const NEGATIVE_STRESS_TERMS = [
  'gestresst',
  'angespannt',
  'überfordert',
  'nervös',
  'stress',
  'overwhelmed',
  'anxious',
];

export function clampRecoveryScore(value: number) {
  return Math.min(10, Math.max(1, Math.round(value)));
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function hasTerm(input: string, term: string): boolean {
  const escapedTerm = term.trim().split(/\s+/).map(escapeRegExp).join('\\s+');
  const pattern = new RegExp(`(^|[^\\p{L}\\p{N}])${escapedTerm}($|[^\\p{L}\\p{N}])`, 'u');
  return pattern.test(input);
}

export function createRecoveryInputDraft(seed?: Partial<RecoveryInputDraft>): RecoveryInputDraft {
  return {
    source: seed?.source ?? 'text',
    raw_input_text: seed?.raw_input_text ?? '',
    transcript_text: seed?.transcript_text ?? '',
    sleep_quality_subjective: clampRecoveryScore(seed?.sleep_quality_subjective ?? 5),
    energy_level: clampRecoveryScore(seed?.energy_level ?? 5),
    stress_management_score: clampRecoveryScore(seed?.stress_management_score ?? 5),
    notes: seed?.notes ?? '',
  };
}

function scoreFromTerms(
  input: string,
  baseScore: number,
  positiveTerms: string[],
  negativeTerms: string[],
) {
  let score = baseScore;

  if (positiveTerms.some((term) => hasTerm(input, term))) {
    score += 2;
  }

  if (negativeTerms.some((term) => hasTerm(input, term))) {
    score -= 2;
  }

  return clampRecoveryScore(score);
}

export function deriveRecoveryInputDraftFromText(
  input: string,
  seed?: Partial<RecoveryInputDraft>,
): RecoveryInputDraft {
  const normalizedInput = input.trim().toLowerCase();
  const baseDraft = createRecoveryInputDraft(seed);

  if (!normalizedInput) {
    return baseDraft;
  }

  return {
    ...baseDraft,
    sleep_quality_subjective: scoreFromTerms(
      normalizedInput,
      baseDraft.sleep_quality_subjective,
      POSITIVE_SLEEP_TERMS,
      NEGATIVE_SLEEP_TERMS,
    ),
    energy_level: scoreFromTerms(
      normalizedInput,
      baseDraft.energy_level,
      POSITIVE_ENERGY_TERMS,
      NEGATIVE_ENERGY_TERMS,
    ),
    stress_management_score: scoreFromTerms(
      normalizedInput,
      baseDraft.stress_management_score,
      POSITIVE_STRESS_TERMS,
      NEGATIVE_STRESS_TERMS,
    ),
  };
}
